import { Button, Chip, Link } from '@heroui/react'
import type { StoredLayout } from '../../lib/api'
import { api } from '../../lib/api'
import { layoutKindFor } from '../../lib/branchDesigns'
import { FROM_PROFILE, type MachineDesign } from '../../lib/machineDesign'
import type { ProfileUnitKind } from '../../lib/types'
import { firstScreen } from '../../layout/counter'
import { LayoutSurface } from '../../layout/render'
import { previewBindings } from '../../layout/sample'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Pick, type PickOption } from '../setupKit'
import '../../styles/branchmachines.css'

/** A design's first screen, small; the built-in design is an empty frame. */
export function DesignThumb({ layout, branchName, size = 'sm' }: { layout?: StoredLayout; branchName: string; size?: 'sm' | 'lg' }) {
  const doc = layout ? (layout.published ?? layout.draft) : undefined
  return (
    <span className="bpm-thumb" data-size={size} inert>
      {doc && layout ? (
        <LayoutSurface
          document={doc}
          screen={firstScreen(layout.kind)}
          bindings={previewBindings(doc, branchName, undefined, api.assetURL)}
        />
      ) : (
        <Icon name="designs" size={size === 'lg' ? 28 : 18} />
      )}
    </span>
  )
}

/** The choices a machine's design picker offers, its own first: the profile's. */
export function designOptions(md: MachineDesign, say: ReturnType<typeof useStaffText>['say']): PickOption[] {
  const named = (l: StoredLayout) => l.name || say('admin.branches.unnamedTicket')
  const inherited = md.inherited.layout ? named(md.inherited.layout) : say('eq.src.default')
  const options: PickOption[] = [{ value: FROM_PROFILE, label: say('bpm.fromProfile', { name: inherited }) }]
  if (md.value === '') options.push({ value: '', label: say('eq.sheet.default') })
  for (const l of md.choices) {
    options.push({
      value: l.id,
      label: named(l),
      note: !l.published ? say('eq.inst.notPublished') : l.isDefault ? say('designs.page.default') : undefined,
    })
  }
  // A choice this machine may no longer take: deleted, or another profile's kiosk design.
  if (md.value !== FROM_PROFILE && md.value !== '' && !md.choices.some((l) => l.id === md.value)) {
    options.push({ value: md.value, label: say('eq.sheet.gone') })
  }
  return options
}

/**
 * Which design a machine shows: a thumbnail, the picker, and whether the choice
 * is its own (with the way back to the profile's) or inherited.
 */
export function DesignPicker({
  md,
  kind,
  label,
  branchName,
  disabled,
  size = 'sm',
  onChange,
}: {
  md: MachineDesign
  kind: ProfileUnitKind
  label: string
  branchName: string
  disabled: boolean
  size?: 'sm' | 'lg'
  onChange: (value: string) => void
}) {
  const { say } = useStaffText()
  const own = md.value !== FROM_PROFILE
  const shown = md.current.layout
  const want = layoutKindFor(kind, md.field === 'ticketLayoutId')
  const options = designOptions(md, say)
  return (
    <div className="bpm-pick" data-size={size} data-kind={want ?? undefined}>
      <span className="bpm-pick__label">{label}</span>
      <DesignThumb layout={shown} branchName={branchName} size={size} />
      <div className="bpm-pick__main">
        <Pick size="sm" className="bpm-pick__select" label={label} value={md.value} disabled={disabled} onChange={onChange} options={options} />
        <span className="bpm-pick__meta">
          {own && (
            <Chip size="sm" variant="soft" color="accent">
              {say('eq.src.own')}
            </Chip>
          )}
          {md.current.unpublished && (
            <Chip size="sm" variant="soft" color="warning">
              {say('eq.inst.notPublished')}
            </Chip>
          )}
          {own && !disabled && (
            <Button variant="ghost" size="sm" onPress={() => onChange(FROM_PROFILE)}>
              <Icon name="undo" size={14} /> {say('bpm.useProfile')}
            </Button>
          )}
          {shown && (
            <Link className="bpm-link" href={`/screens#${encodeURIComponent(shown.id)}`}>
              <Icon name="edit" size={14} /> {say('bpm.editDesign')}
            </Link>
          )}
        </span>
      </div>
    </div>
  )
}
